import React, { cloneElement, useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import { FloatingNode, FloatingPortal } from '@floating-ui/react';

import { TextBox } from '@ecp/common/src/text/TextBox';
import usePopup from '@ecp/common/src/components/unstyled/floating/usePopup';

const PopupUnstyled = React.forwardRef(
  (
    {
      open,
      onOpenChange,
      onChange,
      closeWhenOutside,
      onClose,
      rootComponent: RootComponent,
      rootStyleProps,
      headerComponent: HeaderComponent,
      headerProps,
      bodyComponent: BodyComponent,
      bodyProps,
      bottomButtonComponent: BottomButtonComponent,
      bottomRightButtonComponent: BottomRightButtonComponent,
      closable: showClose,
      closeComponent: CloseComponent,
      children,
      showDialog,
    },
    ref
  ) => {
    const { nodeId, getPopupProps, refs } = usePopup({
      open,
      onOpenChange,
      closeWhenOutside,
      onClose,
      showDialog,
    });
    const [isActive, setIsActive] = useState(true);
    const [startY, setStartY] = useState(0);
    const [isOpen, setIsOpen] = useState(true);

    refs.setFloating(ref?.current);

    const close = useCallback(() => {
      onClose?.(false);
    }, [onClose]);

    const handleClose = useCallback(() => {
      close();
    }, [close]);

    const handleHideToday = useCallback(() => {
      onChange();
    }, [onChange]);

    const handleTab = (event) => {
      if (event.key === 'Tab' && !event.shiftKey) {
        refs.floating.current.focus();
      }
    };

    useEffect(() => {
      function handleClickOutside(e) {
        if (
          refs.floating.current &&
          !refs.floating.current.contains(e.target) &&
          bodyProps.headerType === 'accordion'
        ) {
          setIsActive(false);
          onClose?.(false);
        }
      }
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }, [refs.floating]);

    const handleClick = (e) => {
      if (e.target.classList.contains('dropdown-notSelected') && !e.target.classList.contains('dropdown-open')) {
        setIsOpen(false);
      } else {
        setIsOpen(true);
      }
    };

    const handleTouchStart = (e) => {
      const dropdown = document.querySelector('.dropdown-open');
      setStartY(e.touches[0].clientY);
      if (!dropdown && !isOpen) {
        setIsOpen(true);
      }
    };
    const handleTouchEnd = (e) => {
      if (isOpen) {
        const endY = e.changedTouches[0].clientY;
        const diff = startY - endY;

        if (diff > 50 && bodyProps.headerType === 'accordion') {
          refs.floating.current.scrollTop += refs.floating.current.offsetHeight;
        } else if (diff < -50 && bodyProps.headerType === 'accordion') {
          refs.floating.current.scrollTop -= refs.floating.current.offsetHeight;
          setIsActive(false);
          onClose?.(false);
        }
      }
    };
    return (
      <FloatingNode id={nodeId}>
        <FloatingPortal id={nodeId} tabIndex={0}>
          {open && isActive && (
            <RootComponent
              ref={refs.floating}
              onTouchStart={handleTouchStart}
              onTouchEnd={handleTouchEnd}
              onClick={handleClick}
              style={{ ...rootStyleProps }}
              {...getPopupProps()}
            >
              {HeaderComponent &&
                (typeof HeaderComponent === 'string' ? (
                  <TextBox tabIndex={0} {...headerProps}>
                    {HeaderComponent}
                  </TextBox>
                ) : (
                  <HeaderComponent tabIndex={0} {...headerProps} />
                ))}
              {BodyComponent && (
                <BodyComponent tabIndex={0} {...bodyProps}>
                  {children}
                </BodyComponent>
              )}
              {BottomButtonComponent &&
                cloneElement(BottomButtonComponent, {
                  onClick: () => handleClose(),
                  onKeyDown: (e) => handleTab(e),
                })}
              {BottomRightButtonComponent &&
                cloneElement(BottomRightButtonComponent, {
                  onClick: () => handleHideToday(),
                  onKeyDown: (e) => handleTab(e),
                })}
              {showClose &&
                CloseComponent &&
                cloneElement(CloseComponent, {
                  onClick: () => handleClose(),
                  onKeyDown: (e) => handleTab(e),
                })}
            </RootComponent>
          )}
        </FloatingPortal>
      </FloatingNode>
    );
  }
);

PopupUnstyled.propTypes = {
  showDialog: PropTypes.bool,
  open: PropTypes.bool,
  onOpenChange: PropTypes.func,
  closeWhenOutside: PropTypes.bool,
  onClose: PropTypes.func,
  onChange: PropTypes.func,
  rootComponent: PropTypes.oneOfType([PropTypes.elementType, PropTypes.element, PropTypes.string]),
  rootStyleProps: PropTypes.object,
  headerComponent: PropTypes.oneOfType([PropTypes.elementType, PropTypes.element]),
  headerProps: PropTypes.object,
  bodyComponent: PropTypes.oneOfType([PropTypes.elementType, PropTypes.element]),
  bodyProps: PropTypes.object,
  bottomButtonComponent: PropTypes.oneOfType([PropTypes.elementType, PropTypes.element]),
  bottomRightButtonComponent: PropTypes.oneOfType([PropTypes.elementType, PropTypes.element]),
  closable: PropTypes.bool,
  closeComponent: PropTypes.oneOfType([PropTypes.elementType, PropTypes.element]),
  children: PropTypes.node,
};

PopupUnstyled.defaultProps = {
  rootComponent: 'div',
  rootStyleProps: {},
  closeWhenOutside: false,
};

PopupUnstyled.displayName = 'PopupUnstyled';

export default PopupUnstyled;
